import React, { useState, useEffect } from 'react';
import { 
  Building2, School, Users, UserCheck, Plus, ChevronRight, 
  TrendingUp, DollarSign, FileText, Settings, BarChart3, LogIn,
  Eye, EyeOff, Key, Lock, Unlock, MoreVertical, Copy, Check, Upload, Image, Edit
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { compressImage } from '@/utils/imageCompressor';
import { formatDate } from '../../utils/dateTimeUtils';

/**
 * Compact Indian-currency formatter for stat cards. Big amounts (1L+) are
 * shown as ₹4.25 Cr / ₹60 L so the card layout stays clean instead of the
 * raw "₹4,25,36,000" overflowing onto two lines. The full value is still
 * exposed via the `title` tooltip so the user can hover to see exact paisa.
 *
 *   ₹4,25,36,000   →  ₹4.25 Cr   (>= 1 Cr)
 *   ₹60,00,000     →  ₹60 L      (>= 1 L, < 1 Cr)
 *   ₹85,000        →  ₹85K       (>= 1k, < 1 L)   — only used in tight slots
 *   ₹500           →  ₹500       (fall-through, formatted in en-IN)
 */
const formatINRCompact = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1_00_00_000) {
    return `₹${(n / 1_00_00_000).toFixed(n % 1_00_00_000 === 0 ? 0 : 2)} Cr`;
  }
  if (abs >= 1_00_000) {
    return `₹${(n / 1_00_000).toFixed(n % 1_00_000 === 0 ? 0 : 2)} L`;
  }
  return `₹${n.toLocaleString('en-IN')}`;
};
const formatINRFull = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

const GroupDashboard = () => {
  const [groupData, setGroupData] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddCollegeModal, setShowAddCollegeModal] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [collegeForm, setCollegeForm] = useState({
    name: '',
    subdomain: '',
    address: '',
    college_code: '',
    logo: '',
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    // Individual limits (optional)
    max_students: '',
    max_staff: '',
    max_yearly_admissions: '',
    max_yearly_staff: '',
    subscription_end_date: ''
  });
  
  // P0 Features State
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showEditCollegeModal, setShowEditCollegeModal] = useState(false);
  const [collegeCredentials, setCollegeCredentials] = useState(null);
  const [newPassword, setNewPassword] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    address: '',
    college_code: '',
    logo: '',
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    max_students: '',
    max_staff: '',
    max_yearly_admissions: '',
    max_yearly_staff: '',
    subscription_end_date: ''
  });
  const [editLogoPreview, setEditLogoPreview] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const [dailyStats, setDailyStats] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [groupRes, reportRes, dailyRes] = await Promise.all([
        axios.get(`${API_URL}/api/groups/my-group`, { headers }),
        axios.get(`${API_URL}/api/groups/my-group/consolidated-report`, { headers }).catch(() => null),
        axios.get(`${API_URL}/api/groups/my-group/daily-stats`, { headers }).catch(() => null)
      ]);

      const _groupData = groupRes.data;
      // Promote convenience fields from the response onto the group object so the
      // header renderer can use them without an extra prop chain.
      if (_groupData?.group && typeof _groupData.subscription_days_remaining !== 'undefined') {
        _groupData.group.subscription_days_remaining = _groupData.subscription_days_remaining;
      }
      setGroupData(_groupData);
      if (reportRes?.data) setReport(reportRes.data);
      if (dailyRes?.data) setDailyStats(dailyRes.data);
    } catch (err) {
      console.error('Failed to load group data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Logo size must be less than 5MB');
        return;
      }
      try {
        const result = await compressImage(file, { maxDim: 400, quality: 0.85 });
        setLogoPreview(result.dataUrl);
        setCollegeForm({ ...collegeForm, logo: result.dataUrl });
        console.log(`Logo compressed: ${result.width}x${result.height}, ~${result.sizeKB}KB`);
      } catch (err) {
        alert('Failed to process logo: ' + err.message);
      }
    }
  };

  const handleAddCollege = async (e) => {
    e.preventDefault();
    
    // Validate: College validity cannot exceed Group validity
    const groupEndDate = groupData?.subscription?.end_date;
    if (collegeForm.subscription_end_date && groupEndDate) {
      const collegeEnd = new Date(collegeForm.subscription_end_date);
      const groupEnd = new Date(groupEndDate);
      if (collegeEnd > groupEnd) {
        alert(`College validity cannot exceed Group validity (${formatDate(groupEndDate)})`);
        return;
      }
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data - convert empty strings to null for optional fields
      const formData = {
        ...collegeForm,
        max_students: collegeForm.max_students ? parseInt(collegeForm.max_students) : null,
        max_staff: collegeForm.max_staff ? parseInt(collegeForm.max_staff) : null,
        max_yearly_admissions: collegeForm.max_yearly_admissions ? parseInt(collegeForm.max_yearly_admissions) : null,
        max_yearly_staff: collegeForm.max_yearly_staff ? parseInt(collegeForm.max_yearly_staff) : null,
        subscription_end_date: collegeForm.subscription_end_date || null
      };
      
      const res = await axios.post(
        `${API_URL}/api/groups/my-group/add-college`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCredentials(res.data.admin_credentials);
      setCollegeForm({
        name: '', subdomain: '', address: '', college_code: '', logo: '',
        admin_name: '', admin_email: '', admin_phone: '',
        max_students: '', max_staff: '', max_yearly_admissions: '', max_yearly_staff: '',
        subscription_end_date: ''
      });
      setLogoPreview(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add college');
    }
  };
  
  // Auto-fill group validity when opening Add College modal
  const openAddCollegeModal = () => {
    const groupEndDate = groupData?.subscription?.end_date;
    if (groupEndDate) {
      // Format date as YYYY-MM-DD for input field
      const formattedDate = groupEndDate.split('T')[0];
      setCollegeForm(prev => ({
        ...prev,
        subscription_end_date: formattedDate
      }));
    }
    setShowAddCollegeModal(true);
  };

  const handleEnterCollege = async (college) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/groups/my-group/college/${college.id}/enter`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Open college dashboard in new tab with auto-login
      const collegeToken = res.data.access_token;
      const subdomain = college.subdomain;
      // Store the token and redirect
      const loginUrl = `${window.location.origin}/${subdomain}/auto-login?token=${collegeToken}`;
      window.open(loginUrl, '_blank');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to enter college';
      alert(msg);
      // Fallback to login page
      window.open(`/${college.subdomain}/login`, '_blank');
    }
  };

  // P0: View Admin Credentials
  const handleViewCredentials = async (college) => {
    setSelectedCollege(college);
    setActionLoading(true);
    setShowActionsMenu(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_URL}/api/groups/my-group/college/${college.id}/credentials`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCollegeCredentials(res.data);
      setShowCredentialsModal(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to fetch credentials');
    } finally {
      setActionLoading(false);
    }
  };

  // P0: Reset Admin Password
  const handleResetPassword = async (college) => {
    setSelectedCollege(college);
    setShowResetPasswordModal(true);
    setShowActionsMenu(null);
    setNewPassword(null);
  };

  const confirmResetPassword = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/groups/my-group/college/${selectedCollege.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewPassword(res.data.new_password);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reset password');
      setShowResetPasswordModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  // P0: Block/Unblock College
  const handleToggleBlock = async (college) => {
    const action = college.status === 'active' ? 'block' : 'unblock';
    if (!window.confirm(`Are you sure you want to ${action} "${college.name}"?`)) {
      return;
    }
    setShowActionsMenu(null);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/groups/my-group/college/${college.id}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${action} college`);
    }
  };

  // P1: Edit College
  const handleEditCollege = async (college) => {
    setSelectedCollege(college);
    setShowActionsMenu(null);
    
    // Pre-fill form with existing data
    setEditForm({
      name: college.name || '',
      address: college.address || '',
      college_code: college.college_code || '',
      logo: college.logo || '',
      admin_name: college.admin_name || '',
      admin_email: college.admin_email || '',
      admin_phone: college.admin_phone || '',
      max_students: college.subscription?.max_students?.toString() || '',
      max_staff: college.subscription?.max_staff?.toString() || '',
      max_yearly_admissions: college.subscription?.max_yearly_admissions?.toString() || '',
      max_yearly_staff: college.subscription?.max_yearly_staff?.toString() || '',
      subscription_end_date: college.subscription?.end_date?.split('T')[0] || ''
    });
    setEditLogoPreview(college.logo || null);
    setShowEditCollegeModal(true);
  };

  const handleEditLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Logo size must be less than 5MB');
        return;
      }
      try {
        const result = await compressImage(file, { maxDim: 400, quality: 0.85 });
        setEditLogoPreview(result.dataUrl);
        setEditForm({ ...editForm, logo: result.dataUrl });
      } catch (err) {
        alert('Failed to process logo: ' + err.message);
      }
    }
  };

  const confirmEditCollege = async (e) => {
    e.preventDefault();
    
    // Validate: College validity cannot exceed Group validity
    const groupEndDate = groupData?.subscription?.end_date;
    if (editForm.subscription_end_date && groupEndDate) {
      const collegeEnd = new Date(editForm.subscription_end_date);
      const groupEnd = new Date(groupEndDate);
      if (collegeEnd > groupEnd) {
        alert(`College validity cannot exceed Group validity (${formatDate(groupEndDate)})`);
        return;
      }
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare update data - only include changed fields
      const updateData = {
        name: editForm.name || null,
        address: editForm.address || null,
        college_code: editForm.college_code || null,
        logo: editForm.logo || null,
        admin_name: editForm.admin_name || null,
        admin_email: editForm.admin_email || null,
        admin_phone: editForm.admin_phone || null,
        max_students: editForm.max_students ? parseInt(editForm.max_students) : null,
        max_staff: editForm.max_staff ? parseInt(editForm.max_staff) : null,
        max_yearly_admissions: editForm.max_yearly_admissions ? parseInt(editForm.max_yearly_admissions) : null,
        max_yearly_staff: editForm.max_yearly_staff ? parseInt(editForm.max_yearly_staff) : null,
        subscription_end_date: editForm.subscription_end_date || null
      };
      
      await axios.put(
        `${API_URL}/api/groups/my-group/college/${selectedCollege.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('College updated successfully!');
      setShowEditCollegeModal(false);
      setSelectedCollege(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update college');
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const group = groupData?.group || {};
  const colleges = groupData?.colleges || [];
  const stats = groupData?.stats || {};

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="group-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl p-8 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-indigo-200 text-sm font-medium">{group.code}</p>
            <h1 className="text-3xl font-bold mt-1">{group.name}</h1>
            <p className="text-indigo-100 mt-2">{group.description || 'Group of Colleges Dashboard'}</p>
          </div>
          <div className="text-right">
            <p className="text-indigo-200 text-sm">Subscription</p>
            <p className="text-xl font-bold capitalize">{group.subscription?.plan_type || 'Not Set'}</p>
            {group.subscription?.end_date && (
              <p className="text-indigo-200 text-sm">Valid until: {formatDate(group.subscription.end_date)}</p>
            )}
            {/* Days remaining badge — recalculated every page load so the count
                reflects today, not the originally-fetched value. */}
            {(() => {
              const dr = group.subscription_days_remaining;
              if (typeof dr !== 'number') return null;
              let cls = 'bg-green-500/20 text-green-100 border-green-400/30';
              if (dr <= 7) cls = 'bg-red-500/30 text-red-50 border-red-300/40';
              else if (dr <= 30) cls = 'bg-yellow-500/30 text-yellow-50 border-yellow-300/40';
              const label = dr < 0
                ? `Expired ${Math.abs(dr)} days ago`
                : dr === 0
                ? 'Expires today'
                : `${dr} days remaining`;
              return (
                <span
                  className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold border ${cls}`}
                  data-testid="group-subscription-days-remaining"
                >
                  {label}
                </span>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Colleges</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_colleges || 0}</p>
              <p className="text-xs text-gray-400 mt-1">
                {stats.remaining_slots || 0} slots remaining
              </p>
            </div>
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <School className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Students</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_students || 0}</p>
            </div>
            <div className="p-2.5 bg-green-100 rounded-xl">
              <Users className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Staff</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_staff || 0}</p>
            </div>
            <div className="p-2.5 bg-purple-100 rounded-xl">
              <UserCheck className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-5">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500">Fee Collected</p>
              <p
                className="text-base lg:text-lg font-bold text-green-700 whitespace-nowrap leading-tight"
                title={formatINRFull(report?.totals?.fee_collected)}
                data-testid="group-fee-collected"
              >
                {formatINRCompact(report?.totals?.fee_collected)}
              </p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-5">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500">Pending Fees</p>
              <p
                className="text-base lg:text-lg font-bold text-red-600 whitespace-nowrap leading-tight"
                title={formatINRFull(report?.totals?.fee_pending)}
                data-testid="group-fee-pending"
              >
                {formatINRCompact(report?.totals?.fee_pending)}
              </p>
            </div>
            <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
              <FileText className="h-5 w-5 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">New Admissions</p>
              <p className="text-2xl font-bold text-indigo-700">{report?.totals?.new_admissions || 0}</p>
              <p className="text-xs text-gray-400 mt-1">This month</p>
            </div>
            <div className="p-2.5 bg-indigo-100 rounded-xl">
              <TrendingUp className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Daily Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-green-100 text-sm font-medium">Today's Collection</p>
              <p
                className="text-2xl lg:text-3xl font-bold mt-1 whitespace-nowrap leading-tight"
                title={formatINRFull(dailyStats?.daily_collection)}
              >
                {formatINRCompact(dailyStats?.daily_collection)}
              </p>
              <p className="text-green-200 text-xs mt-1">{dailyStats?.daily_transactions || 0} transactions</p>
            </div>
            <div className="p-3 bg-white/20 rounded-xl flex-shrink-0">
              <DollarSign className="h-8 w-8" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-red-500 to-rose-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-red-100 text-sm font-medium">Today's Expenses</p>
              <p
                className="text-2xl lg:text-3xl font-bold mt-1 whitespace-nowrap leading-tight"
                title={formatINRFull(dailyStats?.daily_expenses)}
              >
                {formatINRCompact(dailyStats?.daily_expenses)}
              </p>
              <p className="text-red-200 text-xs mt-1">{dailyStats?.date}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-xl flex-shrink-0">
              <FileText className="h-8 w-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Colleges Section */}
      <div className="bg-white rounded-xl shadow-lg mb-8">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">My Colleges</h2>
            <p className="text-sm text-gray-500">
              {stats.total_colleges} of {stats.max_colleges} colleges
            </p>
          </div>
          {stats.remaining_slots > 0 && (
            <button
              onClick={() => openAddCollegeModal()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
              Add College
            </button>
          )}
        </div>

        <div className="p-6">
          {colleges.length === 0 ? (
            <div className="text-center py-12">
              <School className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No colleges added yet</p>
              <button
                onClick={() => openAddCollegeModal()}
                className="mt-4 text-blue-600 hover:underline"
              >
                Add your first college
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {colleges.map((college) => (
                <div
                  key={college.id}
                  className="border rounded-xl p-4 hover:border-blue-500 hover:shadow-md transition-all relative"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">
                        {college.name}
                      </h3>
                      <p className="text-sm text-gray-500">{college.subdomain}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        college.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {college.status === 'active' ? 'Active' : 'Blocked'}
                      </span>
                      
                      {/* Actions Menu */}
                      <div className="relative">
                        <button
                          onClick={() => setShowActionsMenu(showActionsMenu === college.id ? null : college.id)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <MoreVertical className="h-5 w-5 text-gray-500" />
                        </button>
                        
                        {showActionsMenu === college.id && (
                          <div className="absolute right-0 top-8 bg-white border rounded-lg shadow-lg z-10 py-1 w-48">
                            <button
                              onClick={() => handleEditCollege(college)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-blue-600"
                            >
                              <Edit className="h-4 w-4" />
                              Edit College
                            </button>
                            <button
                              onClick={() => handleViewCredentials(college)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Eye className="h-4 w-4" />
                              View Credentials
                            </button>
                            <button
                              onClick={() => handleResetPassword(college)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Key className="h-4 w-4" />
                              Reset Password
                            </button>
                            <button
                              onClick={() => handleToggleBlock(college)}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                                college.status === 'active' ? 'text-red-600' : 'text-green-600'
                              }`}
                            >
                              {college.status === 'active' ? (
                                <>
                                  <Lock className="h-4 w-4" />
                                  Block College
                                </>
                              ) : (
                                <>
                                  <Unlock className="h-4 w-4" />
                                  Unblock College
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <div className="flex gap-4">
                      <span className="text-gray-500">
                        <Users className="h-4 w-4 inline mr-1" />
                        {college.student_count || 0}
                      </span>
                      <span className="text-gray-500">
                        <UserCheck className="h-4 w-4 inline mr-1" />
                        {college.staff_count || 0}
                      </span>
                    </div>
                    <button
                      onClick={() => handleEnterCollege(college)}
                      className="text-blue-600 hover:bg-blue-50 px-3 py-1 rounded flex items-center gap-1"
                    >
                      <LogIn className="h-4 w-4" />
                      Enter
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Consolidated Report */}
      {report && report.colleges?.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg mb-8">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              College Performance Overview
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 font-semibold text-gray-700">College</th>
                  <th className="text-center p-4 font-semibold text-gray-700">Students</th>
                  <th className="text-center p-4 font-semibold text-gray-700">Staff</th>
                  <th className="text-center p-4 font-semibold text-gray-700">New Admissions</th>
                  <th className="text-right p-4 font-semibold text-gray-700">Fee Collected</th>
                  <th className="text-right p-4 font-semibold text-gray-700">Pending Fees</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {report.colleges.map((college) => (
                  <tr key={college.id} className="hover:bg-gray-50">
                    <td className="p-4">
                      <p className="font-medium text-gray-900">{college.name}</p>
                      <p className="text-sm text-gray-500">{college.subdomain}</p>
                    </td>
                    <td className="p-4 text-center">{college.students}</td>
                    <td className="p-4 text-center">{college.staff}</td>
                    <td className="p-4 text-center">
                      <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-sm font-medium">
                        {college.new_admissions || 0}
                      </span>
                    </td>
                    <td className="p-4 text-right font-medium text-green-700">
                      ₹{(college.fee_collected || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="p-4 text-right font-medium text-red-600">
                      ₹{(college.fee_pending || 0).toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-bold">
                  <td className="p-4">Total</td>
                  <td className="p-4 text-center">{report.totals.students}</td>
                  <td className="p-4 text-center">{report.totals.staff}</td>
                  <td className="p-4 text-center">{report.totals.new_admissions || 0}</td>
                  <td className="p-4 text-right text-green-700">₹{(report.totals.fee_collected || 0).toLocaleString('en-IN')}</td>
                  <td className="p-4 text-right text-red-600">₹{(report.totals.fee_pending || 0).toLocaleString('en-IN')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Activities */}
      {report?.recent_activities?.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg mb-8">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Recent Activities
            </h2>
          </div>
          <div className="divide-y">
            {report.recent_activities.map((activity, idx) => (
              <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{activity.name}</p>
                    <p className="text-xs text-gray-500">{activity.detail} | {activity.college}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">{activity.date}</p>
                  <p className="text-xs text-blue-600 font-mono">{activity.admission_number}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add College Modal */}
      {showAddCollegeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Add New College</h2>
              <p className="text-sm text-gray-500">
                {stats.remaining_slots} slots remaining
              </p>
            </div>
            
            {credentials ? (
              <div className="p-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <h3 className="font-bold text-green-800 mb-2">✅ College Added Successfully!</h3>
                  <p className="text-sm text-green-700 mb-3">Admin credentials:</p>
                  <div className="space-y-2 text-sm bg-white p-3 rounded border">
                    <p><strong>Email:</strong> {credentials.email}</p>
                    <p><strong>Password:</strong> {credentials.password}</p>
                  </div>
                  <p className="text-xs text-green-600 mt-3">
                    Please save these credentials and share with the college admin.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setCredentials(null);
                    setShowAddCollegeModal(false);
                  }}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleAddCollege} className="p-6 space-y-4">
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">College Logo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                      ) : (
                        <Image className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700">
                        <Upload className="w-4 h-4" />
                        Upload Logo
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG (Max 2MB)</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">College Name *</label>
                  <input
                    type="text"
                    value={collegeForm.name}
                    onChange={(e) => setCollegeForm({ ...collegeForm, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="ABC Engineering College"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant Code (URL Path) *
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 whitespace-nowrap">cloudcampus.site/</span>
                    <input
                      type="text"
                      value={collegeForm.subdomain}
                      onChange={(e) => setCollegeForm({ ...collegeForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })}
                      className="flex-1 px-3 py-2 border rounded-lg"
                      placeholder="abcengg"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Only lowercase letters and numbers allowed</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    College Code * <span className="text-xs text-blue-600">(Used in Student ID)</span>
                  </label>
                  <input
                    type="text"
                    value={collegeForm.college_code || ''}
                    onChange={(e) => setCollegeForm({ ...collegeForm, college_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                    className="w-full px-3 py-2 border rounded-lg uppercase"
                    placeholder="ABCENG"
                    maxLength="10"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Student ID: <code className="bg-gray-100 px-1 rounded">{collegeForm.college_code || 'CODE'}BED2025001</code>
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={collegeForm.address}
                    onChange={(e) => setCollegeForm({ ...collegeForm, address: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Full Address"
                  />
                </div>
                
                {/* Individual Limits Section */}
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700 mb-1">Individual College Limits</p>
                  <p className="text-xs text-gray-500 mb-3">Leave empty to use Group subscription limits</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Max Students</label>
                      <input
                        type="number"
                        value={collegeForm.max_students}
                        onChange={(e) => setCollegeForm({ ...collegeForm, max_students: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        placeholder="Unlimited"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Max Staff</label>
                      <input
                        type="number"
                        value={collegeForm.max_staff}
                        onChange={(e) => setCollegeForm({ ...collegeForm, max_staff: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        placeholder="Unlimited"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Yearly Admissions</label>
                      <input
                        type="number"
                        value={collegeForm.max_yearly_admissions}
                        onChange={(e) => setCollegeForm({ ...collegeForm, max_yearly_admissions: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        placeholder="Unlimited"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Yearly Staff</label>
                      <input
                        type="number"
                        value={collegeForm.max_yearly_staff}
                        onChange={(e) => setCollegeForm({ ...collegeForm, max_yearly_staff: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        placeholder="Unlimited"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <label className="block text-xs text-gray-600 mb-1">
                      Validity End Date 
                      <span className="text-gray-400 ml-1">
                        (Max: {groupData?.subscription?.end_date ? formatDate(groupData.subscription.end_date) : 'N/A'})
                      </span>
                    </label>
                    <input
                      type="date"
                      value={collegeForm.subscription_end_date}
                      onChange={(e) => setCollegeForm({ ...collegeForm, subscription_end_date: e.target.value })}
                      max={groupData?.subscription?.end_date?.split('T')[0] || ''}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Cannot exceed Group validity. Pre-filled with Group end date.
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700 mb-3">College Admin Details</p>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={collegeForm.admin_name}
                      onChange={(e) => setCollegeForm({ ...collegeForm, admin_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Admin Name *"
                      required
                    />
                    <input
                      type="email"
                      value={collegeForm.admin_email}
                      onChange={(e) => setCollegeForm({ ...collegeForm, admin_email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Admin Email *"
                      required
                    />
                    <input
                      type="tel"
                      value={collegeForm.admin_phone}
                      onChange={(e) => setCollegeForm({ ...collegeForm, admin_phone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Admin Phone *"
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700">
                    Add College
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddCollegeModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* View Credentials Modal */}
      {showCredentialsModal && collegeCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Admin Credentials
              </h2>
              <p className="text-sm text-gray-500">{selectedCollege?.name}</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Admin Name</p>
                    <p className="font-medium">{collegeCredentials.admin_name}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium">{collegeCredentials.admin_email}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(collegeCredentials.admin_email)}
                    className="p-2 hover:bg-gray-200 rounded"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-500" />}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="font-medium">{collegeCredentials.admin_phone || 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                Password is not visible. Click Reset Password to generate a new one.
              </p>
              
              <button
                onClick={() => {
                  setShowCredentialsModal(false);
                  setCollegeCredentials(null);
                }}
                className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Key className="h-5 w-5" />
                Reset Admin Password
              </h2>
              <p className="text-sm text-gray-500">{selectedCollege?.name}</p>
            </div>
            
            <div className="p-6">
              {newPassword ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="font-bold text-green-800 mb-2">✅ Password Reset Successful!</h3>
                    <p className="text-sm text-green-700 mb-3">New password:</p>
                    <div className="flex items-center gap-2 bg-white p-3 rounded border">
                      <code className="flex-1 font-mono text-lg">{newPassword}</code>
                      <button
                        onClick={() => copyToClipboard(newPassword)}
                        className="p-2 hover:bg-gray-100 rounded"
                      >
                        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-500" />}
                      </button>
                    </div>
                    <p className="text-xs text-green-600 mt-3">
                      Please save this password and share it with the College Admin.
                    </p>
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setNewPassword(null);
                    }}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      ⚠️ Are you sure you want to Reset Admin Password for <strong>{selectedCollege?.name}</strong>?
                    </p>
                    <p className="text-xs text-yellow-600 mt-2">
                      The old password will no longer work.
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={confirmResetPassword}
                      disabled={actionLoading}
                      className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionLoading ? 'Resetting...' : 'Yes, Reset Password'}
                    </button>
                    <button
                      onClick={() => setShowResetPasswordModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit College Modal */}
      {showEditCollegeModal && selectedCollege && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Edit College
              </h2>
              <p className="text-sm text-gray-500">{selectedCollege.name}</p>
            </div>
            
            <form onSubmit={confirmEditCollege} className="p-6 space-y-4">
              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">College Logo</label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50">
                    {editLogoPreview ? (
                      <img src={editLogoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                    ) : (
                      <Image className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700">
                      <Upload className="w-4 h-4" />
                      Change Logo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleEditLogoUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG (Max 2MB)</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">College Name *</label>
                <input
                  type="text"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">College Code</label>
                <input
                  type="text"
                  value={editForm.college_code}
                  onChange={(e) => setEditForm({...editForm, college_code: e.target.value.toUpperCase()})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., GNEC"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  value={editForm.address}
                  onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                />
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Admin Details</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name</label>
                    <input
                      type="text"
                      value={editForm.admin_name}
                      onChange={(e) => setEditForm({...editForm, admin_name: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
                    <input
                      type="email"
                      value={editForm.admin_email}
                      onChange={(e) => setEditForm({...editForm, admin_email: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Phone</label>
                    <input
                      type="tel"
                      value={editForm.admin_phone}
                      onChange={(e) => setEditForm({...editForm, admin_phone: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Subscription & Limits</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Students</label>
                    <input
                      type="number"
                      value={editForm.max_students}
                      onChange={(e) => setEditForm({...editForm, max_students: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="-1 = Unlimited"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Staff</label>
                    <input
                      type="number"
                      value={editForm.max_staff}
                      onChange={(e) => setEditForm({...editForm, max_staff: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="-1 = Unlimited"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Admissions</label>
                    <input
                      type="number"
                      value={editForm.max_yearly_admissions}
                      onChange={(e) => setEditForm({...editForm, max_yearly_admissions: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="-1 = Unlimited"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Staff</label>
                    <input
                      type="number"
                      value={editForm.max_yearly_staff}
                      onChange={(e) => setEditForm({...editForm, max_yearly_staff: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="-1 = Unlimited"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Validity End Date
                    {groupData?.subscription?.end_date && (
                      <span className="text-xs text-gray-500 ml-2">
                        (Max: {formatDate(groupData.subscription.end_date)})
                      </span>
                    )}
                  </label>
                  <input
                    type="date"
                    value={editForm.subscription_end_date}
                    onChange={(e) => setEditForm({...editForm, subscription_end_date: e.target.value})}
                    max={groupData?.subscription?.end_date?.split('T')[0]}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditCollegeModal(false);
                    setSelectedCollege(null);
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Click outside to close action menu */}
      {showActionsMenu && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setShowActionsMenu(null)}
        />
      )}
    </div>
  );
};

export default GroupDashboard;
